<template>
  <div class="ret-alarms-panel">
    <v-card>

      <v-card-title class="subtitle-1">
        <h4>{{ $t('visuals.replay') }}</h4>
        <v-spacer/>
        <span>{{ itemIndex + 1 }} / {{ itemsCount }}</span>
      </v-card-title>

      <v-divider/>

      <v-card-text class="pb-0">
        <v-btn text icon :title="$t('start')"
               :disabled="playing"
               @click="start"
        >
          <v-icon>play_circle_outline</v-icon>
        </v-btn>
        <v-btn text icon :title="$t('pause')"
               :disabled="!playing"
               @click="pause"
        >
          <v-icon>pause_circle_outline</v-icon>
        </v-btn>
        <v-btn text icon :title="$t('visuals.prev')"
               :disabled="time === from"
               @click="prev"
        >
          <v-icon>skip_previous</v-icon>
        </v-btn>
        <v-btn text icon :title="$t('visuals.next')"
               :disabled="!nextItemTime"
               @click="next"
        >
          <v-icon>skip_next</v-icon>
        </v-btn>
        <v-menu bottom>
          <template v-slot:activator="{ on }">
            <v-btn text icon v-on="on" :title="$t('visuals.speed')">
              <v-icon>speed</v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item
              v-for="item in speeds"
              :key="item.value"
              :disabled="item.value === speed"
              @click="speed = item.value"
            >{{ item.text }}
            </v-list-item>
          </v-list>
        </v-menu>
        <v-slider dense class="mt-2"
                  v-model="time"
                  :min="from"
                  :max="to"
                  :step="1"/>
      </v-card-text>

      <v-divider/>

      <v-card-text>
        <v-btn icon small disabled>
          <v-icon small>access_time</v-icon>
        </v-btn>
        <span class="gray--text">&nbsp;{{ timestamp }}</span>
        <span class="gray--text" v-if="nextItemTime">&nbsp;|&nbsp;{{ $t('visuals.next-update') }}
          {{ nextDiff }}</span>
      </v-card-text>

      <template v-for="([id, diff]) in Object.entries(diffs)">
        <v-divider :key="`divider-${id}`"/>

        <v-list-item :key="id">
          <v-list-item-content>
            <v-list-item-title v-text="diff.fullPath"/>
            <v-list dense class="py-0">
              <v-list-item class="pa-0 justify-space-between"
                           v-for="([name, value]) in Object.entries(diff.variables)" :key="name">
                <v-list-item-action class="variable-name">{{translateVariableName(name)}}</v-list-item-action>
                <v-list-item-action class="ma-0 text-right">
                  {{convertValue(diff.deviceTyp, name, value.from)}}&rarr;{{convertValue(diff.deviceTyp, name, value.to)}}
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-list-item-content>
        </v-list-item>
      </template>

    </v-card>
  </div>
</template>

<style scoped>
.ret-alarms-panel {
  position: fixed;
  top: 30vh;
  right: 10px;
  min-height: 103px;
  max-height: 65vh;
  width: 26em;
  overflow-y: auto;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  opacity: 0.8;
}

.variable-name {
  font-size: 0.8125rem;
  font-weight: 500;
}
</style>

<script>
import moment from 'moment'
import { translateVariableValue, translateVariableName } from '../../helpers/translation'

const tickDuration = 250 // ms

export default {
  name: 'replay-viewer',
  props: {
    from: Number,
    to: Number,
    itemsCount: Number,
    itemIndex: Number,
    diffs: Object,
    prevItemTime: Number,
    nextItemTime: Number
  },
  data () {
    return {
      deviceTypes: this.$store.state.deviceTypes,
      speeds: [
        { text: this.$t('visuals.slower'), value: 1/8 },
        { text: this.$t('visuals.slow'), value: 1/4 },
        { text: this.$t('visuals.real'), value: 1 },
        { text: this.$t('visuals.fast'), value: 4 },
        { text: this.$t('visuals.faster'), value: 8 }
      ],
      speed: 1,
      time: this.from,
      playing: false,
      loop: null
    }
  },
  computed: {
    timestamp () {
      return moment(this.time).format('DD.MM.YYYY HH:mm:ss')
    },
    isRealtime () {
      return this.speed === 1
    },
    nextDiff () {
      const { time, nextItemTime } = this
      return moment.duration(nextItemTime - time).format()
    }
  },
  watch: {
    time () {
      this.$emit('time', this.time)
    }
  },
  methods: {
    translateVariableName,
    convertValue (deviceTypeName, variableName, value) {
      const { deviceTypes } = this
      const deviceType = deviceTypes.find(({ name }) => name === deviceTypeName)
      const variableConfig = deviceType.variables[variableName]
      const code = (variableConfig && variableConfig.type === 'category' && variableConfig.config[value])
        ? variableConfig.config[value].code : value
      return translateVariableValue(code)
    },
    start () {
      this.playing = true
      this.tick()
    },
    pause () {
      this.playing = false
    },
    prev () {
      this.time = this.prevItemTime
    },
    next () {
      this.time = this.nextItemTime
    },
    tick () {
      const { loop, speed, nextItemTime, from, to } = this
      if (loop) {
        clearTimeout(loop)
      }

      this.time += speed * tickDuration

      if (this.time >= to) {
        this.playing = false // auto pause at the end of time interval
        this.time = from // and reset time to start of interval
      }
      if (this.time >= nextItemTime) { // do not skip events
        this.time = nextItemTime
      }

      if (this.playing) {
        this.loop = setTimeout(() => this.tick(), tickDuration)
      }
    }
  }
}
</script>
